import { getCookie, setCookie } from './tracking.utils';

export const DEVICE_FINGERPRINT_KEY = 'tp_device_fingerprint';
const FINGERPRINT_COOKIE_DAYS = 365;

// In-memory cache to avoid repeated DOM / storage queries during the same runtime session
let cachedFingerprint: string | null = null;

/**
 * Multi-round 256-bit synchronous hash function producing a 64-character hex string.
 * This runs synchronously without requiring async Web Crypto API, making it safe
 * for Angular HTTP interceptors, synchronous guards, and all browser environments.
 */
export const hashStringToHex64 = (str: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  let h3 = 0x7fed211b;
  let h4 = 0x9e3779b9;
  let h5 = 0x6a09e667;
  let h6 = 0xbb67ae85;
  let h7 = 0x3c6ef372;
  let h8 = 0xa54ff53a;

  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ (ch << 1), 1597334677);
    h3 = Math.imul(h3 ^ (ch << 2), 380140421);
    h4 = Math.imul(h4 ^ (ch << 3), 2840853829);
    h5 = Math.imul(h5 ^ (ch << 4), 3344921057);
    h6 = Math.imul(h6 ^ (ch << 5), 726179249);
    h7 = Math.imul(h7 ^ (ch << 6), 1723237381);
    h8 = Math.imul(h8 ^ (ch << 7), 2166136261);
  }

  const toHex8 = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  return `${toHex8(h1)}${toHex8(h2)}${toHex8(h3)}${toHex8(h4)}${toHex8(h5)}${toHex8(h6)}${toHex8(h7)}${toHex8(h8)}`;
};

/**
 * Collects a subtle 2D canvas drawing signature to capture GPU, font rasterization,
 * and antialiasing characteristics unique to the client device.
 */
const getCanvasFingerprint = (): string => {
  try {
    if (typeof document === 'undefined') return '';
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 40;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial", sans-serif';
    ctx.fillStyle = '#f60';
    ctx.fillRect(120, 1, 60, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('TenantPay,Security!', 2, 12);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('TenantPay,Security!', 4, 14);

    return canvas.toDataURL();
  } catch {
    return '';
  }
};

/**
 * Generates cryptographic random entropy (fallback to pseudo-random if crypto unavailable).
 */
const getRandomEntropy = (): string => {
  try {
    if (typeof crypto !== 'undefined') {
      if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      if (typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint32Array(4);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(8, '0')).join('-');
      }
    }
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Gathers device & browser entropy signals available on the client.
 */
const collectBrowserEntropy = (): string => {
  if (typeof window === 'undefined') return getRandomEntropy();

  const screenInfo = window.screen
    ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
    : '';
  const pixelRatio = window.devicePixelRatio || 1;
  const timeZone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
  const tzOffset = new Date().getTimezoneOffset();
  const language = window.navigator?.language || '';
  const languages = (window.navigator?.languages || []).join(',');
  const platform = window.navigator?.platform || '';
  const hardwareConcurrency = window.navigator?.hardwareConcurrency || '';
  const canvas = getCanvasFingerprint();
  const randomSeed = getRandomEntropy();

  return [
    screenInfo,
    pixelRatio,
    timeZone,
    tzOffset,
    language,
    languages,
    platform,
    hardwareConcurrency,
    canvas,
    randomSeed,
  ].join('|');
};

/**
 * Retrieves the persistent device fingerprint, or creates, stores, and returns a new one.
 * The fingerprint is stored in localStorage and synchronized with a fallback cookie.
 * Total format: `dfp_<64-character-hex>` (length: 68 characters, well within BE 255-character limit).
 */
export const getDeviceFingerprint = (): string => {
  if (cachedFingerprint) {
    return cachedFingerprint;
  }

  let storedInLS: string | null = null;
  let storedInCookie: string | null = null;

  try {
    if (typeof localStorage !== 'undefined') {
      storedInLS = localStorage.getItem(DEVICE_FINGERPRINT_KEY);
    }
  } catch {}

  try {
    storedInCookie = getCookie(DEVICE_FINGERPRINT_KEY);
  } catch {}

  const existing = storedInLS || storedInCookie;
  if (existing && existing.startsWith('dfp_') && existing.length <= 255) {
    cachedFingerprint = existing;

    // Sync across storage mechanisms if one was cleared
    try {
      if (!storedInLS && typeof localStorage !== 'undefined') {
        localStorage.setItem(DEVICE_FINGERPRINT_KEY, existing);
      }
    } catch {}

    try {
      if (!storedInCookie) {
        setCookie(DEVICE_FINGERPRINT_KEY, existing, FINGERPRINT_COOKIE_DAYS);
      }
    } catch {}

    return existing;
  }

  // Generate new stable fingerprint
  const entropy = collectBrowserEntropy();
  const hexHash = hashStringToHex64(entropy);
  const newFingerprint = `dfp_${hexHash}`;

  cachedFingerprint = newFingerprint;

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DEVICE_FINGERPRINT_KEY, newFingerprint);
    }
  } catch {}

  try {
    setCookie(DEVICE_FINGERPRINT_KEY, newFingerprint, FINGERPRINT_COOKIE_DAYS);
  } catch {}

  return newFingerprint;
};

/**
 * Resets the in-memory cache (primarily used for unit testing).
 */
export const resetDeviceFingerprintCache = (): void => {
  cachedFingerprint = null;
};

/**
 * Detects human-readable client device and browser names for login payloads.
 * This populates `device` and `browser` in LoginDto for manager alert emails.
 */
export const getClientDeviceInfo = (): { device: string; browser: string } => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { device: 'Unknown device', browser: 'Unknown browser' };
  }

  const userAgent = navigator.userAgent || '';

  // Browser detection
  let browser = 'Unknown browser';
  if (/Edg\//i.test(userAgent)) {
    const match = userAgent.match(/Edg\/([0-9.]+)/);
    browser = match ? `Edge ${match[1].split('.')[0]}` : 'Microsoft Edge';
  } else if (/Chrome\//i.test(userAgent) && !/Chromium|Edg/i.test(userAgent)) {
    const match = userAgent.match(/Chrome\/([0-9.]+)/);
    browser = match ? `Chrome ${match[1].split('.')[0]}` : 'Chrome';
  } else if (/Safari\//i.test(userAgent) && !/Chrome|Chromium|Edg/i.test(userAgent)) {
    const match = userAgent.match(/Version\/([0-9.]+)/);
    browser = match ? `Safari ${match[1].split('.')[0]}` : 'Safari';
  } else if (/Firefox\//i.test(userAgent)) {
    const match = userAgent.match(/Firefox\/([0-9.]+)/);
    browser = match ? `Firefox ${match[1].split('.')[0]}` : 'Firefox';
  } else if (/OPR\/|Opera\//i.test(userAgent)) {
    browser = 'Opera';
  }

  // Device / OS detection
  let device = 'Unknown device';
  if (/iPhone/i.test(userAgent)) {
    device = 'iPhone';
  } else if (/iPad/i.test(userAgent)) {
    device = 'iPad';
  } else if (/Android/i.test(userAgent)) {
    device = 'Android';
  } else if (/Windows/i.test(userAgent)) {
    device = 'Windows';
  } else if (/Macintosh|Mac OS X/i.test(userAgent)) {
    device = 'Mac';
  } else if (/Linux/i.test(userAgent)) {
    device = 'Linux';
  }

  return { device, browser };
};
